import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  TrendingUp, Shield, Zap, MessageCircle, Star,
  ChevronRight, Loader2, Crown, ArrowUp
} from 'lucide-react';

const TIER_COLORS = {
  elite: { bg: 'bg-gradient-to-r from-amber-500 to-yellow-400', text: 'text-amber-900', ring: 'ring-amber-400', icon: Crown },
  trending: { bg: 'bg-gradient-to-r from-cyan-500 to-blue-500', text: 'text-white', ring: 'ring-cyan-400', icon: Zap },
  active: { bg: 'bg-cyan-500', text: 'text-white', ring: 'ring-cyan-400', icon: TrendingUp },
  needs_improvement: { bg: 'bg-gray-400', text: 'text-white', ring: 'ring-gray-300', icon: ArrowUp },
};

const AREA_ICONS = {
  reliability: Shield,
  engagement: Zap,
  consistency: MessageCircle,
  client_feedback: Star,
};

const AREA_COLORS = {
  reliability: 'text-emerald-600 bg-emerald-100',
  engagement: 'text-violet-600 bg-violet-100',
  consistency: 'text-cyan-600 bg-cyan-100',
  client_feedback: 'text-amber-600 bg-amber-100',
};

const AREA_LABELS = {
  reliability: 'Reliability',
  engagement: 'Engagement',
  consistency: 'Consistency',
  client_feedback: 'Client Feedback',
};

const PerformanceScoreCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScore();
  }, []);

  const fetchScore = async () => {
    try {
      const res = await api.get('/performance/my-score');
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.error) return null;

  const { score, tier, breakdown, tips, is_boosted, is_premium, fast_boost, boost_expires_soon } = data;
  const tierStyle = TIER_COLORS[tier.key] || TIER_COLORS.needs_improvement;
  const TierIcon = tierStyle.icon;
  const ptsToBoosted = Math.max(0, 75 - score);

  return (
    <Card data-testid="performance-score-card">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-700">Performance Score</CardTitle>
        <Badge className={`${tierStyle.bg} ${tierStyle.text} border-0 text-[10px] font-bold px-2`}>
          <TierIcon className="w-3 h-3 mr-0.5" />{tier.label}
        </Badge>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Score Circle */}
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ring-4 ${tierStyle.ring} bg-white shadow-lg`}>
            <span className="text-2xl font-bold text-gray-900">{score}</span>
          </div>
          <div className="flex-1">
            {is_premium && (
              <p className="text-sm text-pink-600 font-medium flex items-center gap-1">
                <Crown className="w-3.5 h-3.5" />Premium Active
              </p>
            )}
            {is_boosted && !is_premium && (
              <p className="text-sm text-cyan-600 font-medium flex items-center gap-1">
                <Zap className="w-3.5 h-3.5" />Boosted — Trending
              </p>
            )}
            {!is_boosted && !is_premium && ptsToBoosted <= 15 && (
              <p className="text-sm text-amber-600 font-medium">On Track for Trending</p>
            )}
            {!is_boosted && !is_premium && ptsToBoosted > 15 && (
              <>
                <p className="text-sm text-gray-600 font-medium">{ptsToBoosted} pts to Trending</p>
                {ptsToBoosted <= 25 && (
                  <p className="text-[10px] text-indigo-500 font-medium mt-0.5">Post your last event to boost your visibility</p>
                )}
              </>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[11px] text-gray-400">Based on last 90 days</p>
              {fast_boost > 0 && !boost_expires_soon && (
                <span className="text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">+{fast_boost} boost</span>
              )}
              {fast_boost > 0 && boost_expires_soon && (
                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Visibility dropping — post to stay Trending</span>
              )}
            </div>
          </div>
        </div>

        {/* Breakdown Bars */}
        <div className="space-y-2.5 mb-4">
          {Object.entries(breakdown).map(([key, val]) => {
            const Icon = AREA_ICONS[key] || TrendingUp;
            const color = AREA_COLORS[key] || 'text-gray-600 bg-gray-100';
            const barColor = key === 'reliability' ? 'bg-emerald-500' :
                            key === 'engagement' ? 'bg-violet-500' :
                            key === 'consistency' ? 'bg-cyan-500' : 'bg-amber-500';
            return (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${color}`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <span className="text-xs text-gray-600">{AREA_LABELS[key]}</span>
                  </div>
                  <span className="text-xs font-semibold text-gray-800">{val.score}<span className="text-gray-400 font-normal">/{100}</span></span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className={`${barColor} rounded-full h-1.5 transition-all`} style={{width: `${val.score}%`}} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Top Tip */}
        {tips && tips.length > 0 && (
          <div className="p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-[10px] text-indigo-400 font-medium uppercase tracking-wider mb-0.5">How to Improve</p>
            <p className="text-xs text-indigo-800">{tips[0].tip}</p>
            <span className="text-[10px] text-indigo-500 font-medium">{tips[0].impact}</span>
          </div>
        )}

        {/* Premium Upgrade CTA — only for non-premium talent */}
        {!is_premium && (
          <div
            onClick={() => navigate('/talent/premium')}
            className="mt-3 p-3 premium-card-discovery rounded-lg text-center cursor-pointer hover:scale-[1.01] transition-transform"
            data-testid="upgrade-premium-cta"
          >
            <Crown className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xs font-semibold text-gray-800">Upgrade to Premium</p>
            <p className="text-[10px] text-gray-500">Appear first in search and get more visibility</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceScoreCard;
